/* eslint-disable max-lines */
import { Button } from "@design-lab/shared/ui";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { usePlacementPosition } from "@design-lab/widgets/artboard/model/usePlacementPosition";
import { useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
import { trackPlacementChanged } from "@design-lab/shared/lib/analytics";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTab, designSubTabAtom } from "@design-lab/shared/ui/layouts/state";
import { useEffect, useRef, useMemo } from "react";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useAtom } from "jotai";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { remoteImageStaticUrl } from "common/utils/remote-image";

type SideSwitcherItem = {
  label: string;
  value: PlacementFormPosition;
  image: string | undefined;
  price?: number;
  isSelected: boolean;
  ariaLabel: string;
  disabled?: boolean;
};

export function SideSwitcher() {
  const { tab, setTab, isDesignTab } = useTab();
  const { lens } = useArtworkForm();
  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);
  const { isDesktop } = useResponsive();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};

  const { composition } = useArtboard();
  const frontSurface = composition?.surfaces?.front || composition?.surfaces?.frontLeftChest;
  const backSurface = composition?.surfaces?.back;

  const { getMachineBySide } = useArtwork();
  const frontMachine = getMachineBySide("front");
  const backMachine = getMachineBySide("back");

  // A/B test: Check if tab reordering experiment is active
  const experiments = useExperiments();
  const [activeSubTab, setActiveSubTab] = useAtom(designSubTabAtom);
  const showSubTabs = useMemo(
    () => shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs),
    [experiments?.abDesignLabReorderTabs]
  );
  const hasSubTabs = (tab === "design" || tab === "upload") && showSubTabs;

  // Track previous loading states to detect new loading events
  const prevLoadingRef = useRef({ front: false, back: false });

  // Determine selection state based on context
  let isFrontSelected = false;
  let isBackSelected = false;

  if (hasSubTabs) {
    isFrontSelected = activeSubTab === "front";
    isBackSelected = activeSubTab === "back";
  } else if (isDesignTab) {
    isFrontSelected = tab === "front";
    isBackSelected = tab === "back";
  } else {
    isFrontSelected = ["frontLeftChest", "front"].includes(currentPlacement.placement);
    isBackSelected = currentPlacement.placement === "back";
  }

  const items: SideSwitcherItem[] = [
    {
      label: "Front",
      ariaLabel: "Front button",
      value: selectedFrontPlacement.placement,
      image: frontSurface?.picture,
      disabled: Boolean(sides?.front?.length === 0),
      isSelected: isFrontSelected
    },
    {
      label: "Back",
      ariaLabel: "Back button",
      value: "back",
      disabled: Boolean(sides?.back?.length === 0),
      image: backSurface?.picture,
      price: 4.49,
      isSelected: isBackSelected
    }
  ];

  const handleItemSelect = (item: SideSwitcherItem) => {
    const fromPlacement = currentPlacement.placement;
    const toPlacement = item.value;

    if (fromPlacement !== toPlacement) {
      trackPlacementChanged(fromPlacement, toPlacement);
    }

    currentPlacement.setPlacement(item.value);

    // A/B test: Update sub-tab state instead of changing main tab
    if (hasSubTabs) {
      if (toPlacement.startsWith("front")) {
        setActiveSubTab("front");
      } else {
        setActiveSubTab("back");
      }
    } else if (isDesignTab) {
      if (toPlacement.startsWith("front")) {
        setTab("front");
      } else {
        setTab("back");
      }
    }
  };

  const handleRotate = () => {
    // Determine if currently on front side
    let currentIsOnFront = false;
    if (hasSubTabs) {
      currentIsOnFront = activeSubTab === "front";
    } else if (isDesignTab) {
      currentIsOnFront = tab === "front";
    } else {
      currentIsOnFront = ["frontLeftChest", "front"].includes(currentPlacement.placement);
    }

    const fromPlacement = currentPlacement.placement;
    const toPlacement = currentIsOnFront ? "back" : selectedFrontPlacement?.placement || "front";

    if (fromPlacement !== toPlacement) {
      trackPlacementChanged(fromPlacement, toPlacement);
    }

    currentPlacement.setPlacement(toPlacement);

    // A/B test: Update sub-tab state instead of changing main tab
    if (hasSubTabs) {
      setActiveSubTab(currentIsOnFront ? "back" : "front");
    } else if (isDesignTab) {
      setTab(currentIsOnFront ? "back" : "front");
    }
  };

  useEffect(() => {
    const frontLoading = frontMachine.state.hasTag("loading");
    const backLoading = backMachine.state.hasTag("loading");
    const prevLoading = prevLoadingRef.current;
    // Detect which side just started loading (if any)
    const frontJustStartedLoading = frontLoading && !prevLoading.front;
    const backJustStartedLoading = backLoading && !prevLoading.back;

    // Auto-switch should only happen on upload/design tabs, not on products/sizes tabs
    const shouldAutoSwitch = hasSubTabs ? tab === "upload" || tab === "design" : isDesignTab;

    // Suppress auto-switch when copy flow marks suppression on either side
    if (
      (frontJustStartedLoading && frontMachine.state.context.suppressAutoSwitch) ||
      (backJustStartedLoading && backMachine.state.context.suppressAutoSwitch)
    ) {
      // Update ref and skip switching
      prevLoadingRef.current = { front: frontLoading, back: backLoading };
      return;
    }
    // Switch to the side that just started loading (only on relevant tabs)
    if (shouldAutoSwitch && frontJustStartedLoading) {
      currentPlacement.setPlacement(selectedFrontPlacement.placement || "front");
      if (hasSubTabs) {
        setActiveSubTab("front");
      } else if (isDesignTab) {
        setTab("front");
      }
    } else if (shouldAutoSwitch && backJustStartedLoading) {
      currentPlacement.setPlacement("back");
      if (hasSubTabs) {
        setActiveSubTab("back");
      } else if (isDesignTab) {
        setTab("back");
      }
    }
    // Update the ref for next comparison
    prevLoadingRef.current = { front: frontLoading, back: backLoading };
  }, [
    frontMachine,
    backMachine,
    isDesignTab,
    setTab,
    selectedFrontPlacement.placement,
    currentPlacement,
    hasSubTabs,
    setActiveSubTab,
    tab
  ]);

  if (!composition || !frontSurface || !backSurface) return null;

  // Check if rotate button should be disabled
  const shouldDisableRotate = Boolean(sides?.front?.length === 0 || sides?.back?.length === 0);

  if (isDesktop) {
    // Desktop: Orange banner instead of red badge
    return (
      <div className="absolute left-0 top-0 z-10 flex gap-2 rounded-md bg-gray-200 p-1">
        {items.map(item => (
          <Button
            key={item.value}
            data-testid={`side-switcher-${item.value}-button`}
            aria-label={item.ariaLabel}
            variant="outline"
            className={cn("h-12 w-full px-2", {
              "outline-primary outline-2 outline-offset-[-2px]": item.isSelected
            })}
            onPress={() => handleItemSelect(item)}
            isDisabled={!!item.disabled}
          >
            <span className="inline-flex size-8 items-center justify-center rounded-sm bg-gray-200">
              <img className="max-h-full max-w-full mix-blend-multiply" src={item.image} />
            </span>
            {item.label}
          </Button>
        ))}
      </div>
    );
  }

  // Mobile: Single Rotate button
  return (
    <div className="absolute right-2 top-4 z-10 rounded-md bg-gray-200">
      <Button
        data-testid="mobile-rotate-button"
        aria-label="Rotate to switch between front and back"
        variant="outline"
        className="size-14 flex-col gap-0 px-2"
        onPress={handleRotate}
        isDisabled={shouldDisableRotate}
      >
        <img
          src={remoteImageStaticUrl("rotate-icon.svg")}
          alt="Rotate Image Button"
          className="size-8"
        />
        <span className="text-xs">ROTATE</span>
      </Button>
    </div>
  );
}
